/**
 * Parámetros de búsqueda de la ruta `/mi-cuenta`.
 *
 * Vive en su propio módulo por la misma razón que `busqueda-disponibilidad.ts`:
 * lo necesitan `enrutador.tsx`, que declara la ruta, y `rutas/MiCuenta.tsx`,
 * que es el componente; y esos dos no se pueden importar entre sí.
 *
 * DE DÓNDE SALEN ESTOS PARÁMETROS. Cuando el inicio de sesión con Google falla,
 * better-auth redirige al `errorCallbackURL` —que este frontend fija en
 * `/mi-cuenta`— agregando `?error=<código>` y, a veces,
 * `&error_description=<texto>`. Está en su código, `dist/oauth2/errors.mjs`,
 * función `redirectOnError`, que es el único sitio por donde pasan todos los
 * fallos del callback.
 *
 * Sin esto, un inicio de sesión rechazado dejaría a la persona en una pantalla
 * que dice «hace falta iniciar sesión» sin ninguna pista de que acaba de
 * intentarlo y de por qué no funcionó.
 */

export interface BusquedaMiCuenta {
  /** Código de fallo del inicio de sesión. Ausente = no vino de un fallo. */
  error?: string | undefined
  /** Texto del proveedor, cuando lo manda. */
  error_description?: string | undefined
}

export function validarBusquedaMiCuenta(
  entrada: Record<string, unknown>,
): BusquedaMiCuenta {
  return {
    error: textoOmitido(entrada['error']),
    error_description: textoOmitido(entrada['error_description']),
  }
}

/** Cadena vacía y ausencia son lo mismo: se omite para no ensuciar la URL. */
function textoOmitido(valor: unknown): string | undefined {
  return typeof valor === 'string' && valor.trim() !== ''
    ? valor.trim()
    : undefined
}

/**
 * Explicación en español de los códigos que emite better-auth en el callback,
 * más el `access_denied` que manda Google cuando la persona cancela.
 *
 * Los códigos están tomados de `dist/api/routes/callback.mjs` del paquete
 * instalado (1.6.26), no de memoria. Lo que no esté en el mapa se muestra tal
 * cual: enseñar el código crudo es más útil que un «error desconocido».
 */
export function explicarFalloDeEntrada(codigo: string): string | null {
  switch (codigo) {
    case 'access_denied':
      return 'Cancelaste el permiso en la pantalla de Google, o Google no lo concedió.'
    case 'unable_to_get_user_info':
      return (
        'El servidor no pudo tomar tus datos de Google. La causa más frecuente ' +
        'en este sistema es haber entrado con una cuenta que no pertenece al ' +
        'dominio institucional: el dominio se valida en el servidor antes de ' +
        'crear la sesión.'
      )
    case 'email_not_found':
      return 'Google no entregó ningún correo para esa cuenta.'
    case 'invalid_code':
    case 'no_code':
    case 'invalid_callback_request':
    case 'no_callback_url':
      return 'El regreso desde Google llegó incompleto. Vuelve a intentarlo desde el principio.'
    case 'oauth_provider_not_found':
      return 'Este servidor no tiene configurado el inicio de sesión con Google.'
    case 'unable_to_link_account':
    case 'account_already_linked_to_different_user':
    case "email_doesn't_match":
      return 'Esa cuenta de Google ya está ligada a otro usuario del sistema.'
    default:
      return null
  }
}
